<template>
  <div
    id="search"
    class="search"
    :style="themedButtons"
    @keydown.esc.exact.prevent="closeModal"
  >
    <div
      ref="modalContainer"
      class="search-container"
    >
      <button
        ref="closeButton"
        class="close-button"
        :aria-label="$t('search.closeSearchModal')"
        @click="closeModal"
      >
        <Icon name="lucide:x" />
        {{ $t('search.closeSearch') }}
      </button>
      <SearchBar
        :initial-search-term="route.query.q"
        :search-suggestions="searchSuggestions"
        @search-term="debouncedHandleSuggestions"
        @full-search-initiated="debouncedShowSearchResults"
      />
      <SearchResults
        v-if="showResultsPanel"
        :is-loading="status === 'pending'"
        :results="searchResults"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, useTemplateRef } from 'vue';
import { onClickOutside, useDebounceFn, useScrollLock } from '@vueuse/core';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import SearchBar from './SearchBar.vue';
import SearchResults from './SearchResults.vue';
import { useThemedButtons } from '@/composables/useThemedButtons';
const { themedButtons } = useThemedButtons();

// Modal scroll lock and close modal on outside click
useScrollLock(document.body, true);

const modalContainer = useTemplateRef('modalContainer');
const { deactivate } = useFocusTrap(modalContainer, { immediate: true });

function closeModal() {
  // eslint-disable-next-line no-unused-vars
  const { openSearch, secondary_organizations, query_indices, q, ...currentQuery } = route.query;

  deactivate();

  return navigateTo({
    path: route.path,
    query: currentQuery,
  });
}

onClickOutside(modalContainer, closeModal);

const route = useRoute();
const router = useRouter();
const store = useStore();

const { value: authToken } = useCookie('auth-token');

const { data, status, error, execute } = await useAsyncData(
  `is-${route.query.q}`,
  () => {
    const { query } = route;
    const { q, secondary_organizations, query_indices } = query;

    return store.dispatch('getSearchResults', {
      term: q,
      writableApi: useRuntimeConfig().public.WRITABLE_BASE_API_URL,
      orgIds: secondary_organizations,
      selectedFilters: query_indices,
      token: authToken,
    });
  },
  {
    immediate: false,
  },
);

const showResultsPanel = ref(false);
const searchSuggestions = ref([]);
const searchResults = ref([]);

async function showSearchResults(searchQuery) {
  try {
    router.push({
      path: route.path,
      query: { ...searchQuery },
    });
    await execute();
    return data;
  } catch {
    console.error(error);
  }
}

async function fullSearchInitiated(searchTerm) {
  const currentQuery = { ...route.query };
  const query = { ...currentQuery, q: searchTerm };
  searchSuggestions.value = [];
  showResultsPanel.value = true;

  const searchReturns = await showSearchResults(query);
  searchResults.value = searchReturns.value.data.results;
}

async function handleSuggestions(searchTerm) {
  if (!searchTerm || searchTerm === '') {
    router.push({
      query: { ...route.query, q: '' },
    });
    showResultsPanel.value = false;
    searchSuggestions.value = [];
    return;
  }

  try {
    router.push({
      query: { ...route.query, q: searchTerm },
    });
    await execute();
    searchSuggestions.value = data.value.data.results.slice(0, 3);
  } catch {
    console.error(error);
  }
}

const debouncedHandleSuggestions = useDebounceFn(handleSuggestions, 300);
const debouncedShowSearchResults = useDebounceFn(fullSearchInitiated, 300);

// Initial search
if (route.query.q) {
  showSearchResults(route.query.q);
}

watch(
  () => route.query,
  async () => {
    const hasFilters = route.query?.secondary_organizations?.length || route.query?.query_indices?.length;
    const hasSearchTerm = route.query?.q;

    if (hasFilters || hasSearchTerm) {
      const searchReturns = await showSearchResults(route.query);
      searchResults.value = searchReturns.value?.data?.results;
    }
  },
  {
    deep: true,
    immediate: true,
  },
);
</script>

<style lang="scss" scoped>
@use '../global/scss/mixins/_breakpoints.scss' as bp;

#search {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  background: rgba(34, 34, 34, 0.8);
  z-index: 10;

  .search-container {
    width: 48vw;
    height: 64vh;
    background: transparent;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 16px;
    position: relative;
  }
}

.close-button {
  opacity: 1;
  margin-left: auto;
  display: flex;
  gap: 4px;
  align-items: center;
  background: none;
  padding: 5px;
  border: none;
  color: var(--primary-white);

  font-size: 14px;
  font-weight: 500;
  line-height: 19px;
  letter-spacing: 0.6px;

  &:hover {
    cursor: pointer;
  }
  &:focus-visible {
    outline-offset: 2px;
  }
}

@include bp.screen('<=md') {
  #search {
    --search-bar-height: 74px;
    .search-container {
      position: absolute;
      top: 0;
      padding: 16px 24px;
      width: calc(100vw - 48px);
      height: calc(100vh - 48px);

      .search-bar {
        height: var(--search-bar-height);
      }

      .search-results {
        height: calc(100vh - var(--search-bar-height));
      }
    }
  }
}

@include bp.screen('xs') {
  #search .search-container {
    --search-bar-height: 74px;
    width: 100%;
    height: 100%;
    gap: 0;
    padding: 0;
    .search-bar {
      height: var(--search-bar-height);
      border-radius: 0;
    }
    .search-results {
      border-radius: 0;
      height: calc(100vh - var(--search-bar-height));
    }
    .close-button {
      margin: 6px 6px 7px 0;
      margin-left: auto;
    }
  }
}
</style>
